import { useCallback } from 'react';
import { IEmployeeBudget } from '@/common/integrations/menhir/employee/budgets/budgets.types';
import { MenhirBudgetLogos } from '@/common/integrations/menhir/employee/budgets/budgets.types';
import { DateUtils } from '@/common/utils/date';
import { shouldTreatAsExpired } from '@/web/containers/Budgets/util';
import {
  IconAward,
  IconBabyCarriage,
  IconBanSmoking,
  IconBoltLightning,
  IconBook,
  IconBrain,
  IconBurger,
  IconCarSide,
  IconChair,
  IconDesktop,
  IconFaceGrinStars,
  IconHeadphones,
  IconHeartPulse,
  IconIcons,
  IconShieldHeart,
  IconSuitcaseRolling,
  IconSyringe,
  IconTaxi,
  IconWeightScale,
  IconWheelchairMove,
} from '@coverflex-tech/hypernova';
import { defaultMoney, moneyToString } from '@coverflex-tech/hypernova-utils';
import classNames from 'classnames';
import { TFunction } from 'i18next';
import css from './budgets-home.module.css';

interface GetBudgetIconParams {
  type: MenhirBudgetLogos;
  className?: string;
  height?: number;
  width?: number;
  color?: string;
  colored?: boolean;
}

export const getBudgetIcon = ({
  type,
  className,
  height,
  width,
  color,
  colored = false,
}: GetBudgetIconParams) => {
  const props = {
    className: classNames(className, {
      [css.budgetIcon!]: colored,
    }),
    color,
    width,
    height,
  };
  switch (type) {
    case 'default':
    default:
      return <IconFaceGrinStars {...props} />;
    case 'award':
      return <IconAward {...props} />;
    case 'books':
      return <IconBook {...props} />;
    case 'knowledge':
      return <IconBrain {...props} />;
    case 'food':
      return <IconBurger {...props} />;
    case 'car':
      return <IconCarSide {...props} />;
    case 'leisure':
      return <IconChair {...props} />;
    case 'technology':
      return <IconDesktop {...props} />;
    case 'entertainment':
      return <IconHeadphones {...props} />;
    case 'misc':
      return <IconIcons {...props} />;
    case 'transportation':
      return <IconTaxi {...props} />;
    case 'electricity':
      return <IconBoltLightning {...props} />;
    case 'travel':
      return <IconSuitcaseRolling {...props} />;
    case 'fitness':
      return <IconHeartPulse {...props} />;
    case 'wellness':
      return <IconShieldHeart {...props} />;
    case 'disability':
      return <IconWheelchairMove {...props} />;
    case 'quit_smoking':
      return <IconBanSmoking {...props} />;
    case 'lose_weight':
      return <IconWeightScale {...props} />;
    case 'vaccination':
      return <IconSyringe {...props} />;
    case 'baby':
      return <IconBabyCarriage {...props} />;
  }
};

export const useShouldDisplayDelayedTopUpInfo = () => {
  return useCallback((employeeBudget: IEmployeeBudget) => {
    return Boolean(
      employeeBudget.status === 'active' &&
        employeeBudget.firstTopUpAlreadyBeenMade === false &&
        [undefined, true].includes(
          employeeBudget.applicationTemplate?.singleApplication
        )
    );
  }, []);
};

export function getCardSubtitle(employeeBudget: IEmployeeBudget, t: TFunction) {
  const treatAsExpired = shouldTreatAsExpired(employeeBudget);

  if (treatAsExpired && !!employeeBudget.endedAt) {
    return t('dashboard.budgets.expired_on', {
      date: DateUtils.formatDate(employeeBudget.endedAt),
    });
  }

  // active and recurrent -> renews on x
  if (
    employeeBudget.status === 'active' &&
    employeeBudget.budget.recurrence !== 'one_time' &&
    !!employeeBudget.budget.renewalDate
  ) {
    return t('dashboard.budgets.renews_on', {
      date: DateUtils.formatDate(employeeBudget.budget.renewalDate),
    });
  }

  // one time and ended -> expires on x
  if (
    employeeBudget.status === 'active' &&
    (!!employeeBudget.endedAt || !!employeeBudget.budget.endedAt)
  ) {
    return t('dashboard.budgets.expires_on', {
      date: DateUtils.formatDate(
        employeeBudget.endedAt ?? employeeBudget.budget.endedAt
      ),
    });
  }
  // TODO: cover the case where the budget will expire before it renews

  return '';
}

export function getPercentageOfBudgetUsed(
  budgetEmployee: IEmployeeBudget
): number {
  if (
    !budgetEmployee.pocket ||
    !budgetEmployee.spentAmount ||
    budgetEmployee.spentAmount.amount < 0
  ) {
    return 0;
  }

  if (budgetEmployee.budget.paymentMethod === 'payroll') return 100;

  const totalAmount = budgetEmployee.budget.withAccrual
    ? (budgetEmployee.accumulatedAmount ?? defaultMoney)
    : budgetEmployee.budgetAmount;

  const percent =
    (budgetEmployee.spentAmount.amount / totalAmount.amount) * 100;
  return percent > 100 ? 100 : percent;
}

export const determineBudgetCircleTitle = ({
  employeeBudget,
  isPayRoll,
  isAvailableSoon,
  isSpent,
  t,
}: {
  employeeBudget: IEmployeeBudget;
  isPayRoll: boolean;
  isAvailableSoon: boolean;
  isSpent: boolean;
  t: TFunction;
}) => {
  const circleAmount = isPayRoll
    ? moneyToString(employeeBudget.budgetAmount)
    : moneyToString(employeeBudget.pocket?.availableBalance);

  if (isAvailableSoon) {
    return moneyToString(employeeBudget.budgetAmount);
  }

  return isSpent && !isPayRoll ? t('dashboard.budgets.spent') : circleAmount;
};

export const determineBudgetCircleText = ({
  isPayRoll,
  isAvailableSoon,
  isSpent,
  t,
}: {
  isPayRoll: boolean;
  isAvailableSoon: boolean;
  isSpent: boolean;
  t: TFunction;
}) => {
  if (isPayRoll) {
    return t('dashboard.budgets.assigned');
  }

  if (isAvailableSoon) {
    return t('dashboard.budgets.soon');
  }

  return isSpent ? undefined : t('dashboard.budgets.available');
};
